import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import * as ProductDetailsActions from './../actions/product-details.actions';
import {
  DeleteMandate,
  DeleteMandateError,
  DeleteMandateSuccess,
  GetDeepLinkOptionDetails,
  GetDeepLinkOptionDetailsFailed,
  GetDeepLinkOptionDetailsSuccess,
  GetDeepLinkOptionDetailsSuccessNoChange,
  GetEBUProductDetails,
  GetEBUProductDetailsFailed,
  GetEBUProductDetailsSuccess,
  GetEBUProductDetailsSuccessNoChange,
  GetOptionDetails,
  GetOptionDetailsFailed,
  GetOptionDetailsSuccess,
  GetOptionDetailsSuccessNoChange,
  GetProductAvailableOptions,
  GetProductAvailableOptionsFailed,
  GetProductAvailableOptionsSuccess,
  GetProductAvailableOptionsSuccessNoChange,
  GetProductAvailableSettings,
  GetProductAvailableSettingsFailed,
  GetProductAvailableSettingsSuccess,
  GetProductAvailableSettingsSuccessNoChange,
  GetProductAvailableSubscripionsFailed,
  GetProductAvailableSubscriptions,
  GetProductAvailableSubscriptionsSuccess,
  GetProductAvailableSubscriptionsSuccessNoChange,
  GetProductDetails,
  GetProductDetailsFailed,
  GetProductDetailsSuccess,
  GetProductDetailsSuccessNoChange,
  UpdateEBUOptionActiveStatusSuccess,
} from '../actions/product-details.actions';
import { ProductsService } from '../../services/products.service';
import { IEntity } from '@proximus/entity-manager';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
  distinctUntilChanged,
} from 'rxjs/operators';
import { TechnicalError } from '@dch/products-business-logic';
import { MpayService } from '../../services/mpay.service';
import { ApplicationState, getSelectedProductDetails } from '../reducers';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailsEffects {
  @Effect()
  productDetails$ = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_PRODUCT_DETAILS),
    switchMap((action: GetProductDetails) =>
      this.productsService.getProductsDetails(action.payload).pipe(
        distinctUntilChanged(),
        switchMap((iEntity: IEntity<any>) => {
          if (action.payload.productType === 'epress') {
            return [new GetProductDetailsSuccess(iEntity.value)];
          }
          if (action.payload.productType === 'digitalis') {
            return [new GetProductDetailsSuccess(iEntity.value)];
          }
          if (action.payload.productType === 'mobile') {
            return [
              new GetProductDetailsSuccess(iEntity.value),
              new GetProductAvailableOptions(action.payload),
              new GetProductAvailableSettings(action.payload),
              new GetProductAvailableSubscriptions(action.payload),
            ];
          } else {
            return [
              new GetProductDetailsSuccess(iEntity.value),
              new GetProductAvailableOptions(action.payload),
            ];
          }
        }),
        catchError((error: IEntity<TechnicalError>) => {
          if (error.status === 304) {
            if (action.payload.productType === 'epress') {
              return of(new GetProductDetailsSuccessNoChange());
            }
            if (action.payload.productType === 'digitalis') {
              return of(new GetProductDetailsSuccessNoChange());
            }
            if (action.payload.productType === 'mobile') {
              return of(
                new GetProductDetailsSuccessNoChange(),
                new GetProductAvailableOptions(action.payload),
                new GetProductAvailableSettings(action.payload),
                new GetProductAvailableSubscriptions(action.payload),
              );
            } else {
              return of(
                new GetProductDetailsSuccessNoChange(),
                new GetProductAvailableOptions(action.payload),
              );
            }
          } else {
            return of(
              new GetProductDetailsFailed(error.errorData as TechnicalError),
            );
          }
        }),
      ),
    ),
  );

  @Effect()
  productAvailableOptions$: Observable<Action> = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_PRODUCT_AVAILABLE_OPTIONS),
    switchMap((action: GetProductAvailableOptions) =>
      this.productsService.getProductAvailableOptions(action.payload).pipe(
        map((iEntity: IEntity<any>) => {
          return new GetProductAvailableOptionsSuccess(iEntity.value);
        }),
        catchError((error: IEntity<TechnicalError>) => {
          if (error.status === 304) {
            return of(new GetProductAvailableOptionsSuccessNoChange());
          } else {
            return of(
              new GetProductAvailableOptionsFailed(
                error.errorData as TechnicalError,
              ),
            );
          }
        }),
      ),
    ),
  );

  @Effect()
  productAvailableSettings$: Observable<Action> = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_PRODUCT_AVAILABLE_SETTINGS),
    switchMap((action: GetProductAvailableSettings) =>
      this.productsService.getProductAvailableSettings(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new GetProductAvailableSettingsSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) => {
          if (error.status === 304) {
            return of(new GetProductAvailableSettingsSuccessNoChange());
          } else {
            return of(
              new GetProductAvailableSettingsFailed(
                error.errorData as TechnicalError,
              ),
            );
          }
        }),
      ),
    ),
  );

  @Effect()
  productAvailableSubscriptions$: Observable<Action> = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_PRODUCT_AVAILABLE_SUBSCRIPTIONS),
    switchMap((action: GetProductAvailableSubscriptions) =>
      this.productsService
        .getProductAvailableSubscriptions(action.payload)
        .pipe(
          map(
            (iEntity: IEntity<any>) =>
              new GetProductAvailableSubscriptionsSuccess(iEntity.value),
          ),
          catchError((error: IEntity<TechnicalError>) => {
            if (error.status === 304) {
              return of(new GetProductAvailableSubscriptionsSuccessNoChange());
            } else {
              return of(
                new GetProductAvailableSubscripionsFailed(
                  error.errorData as TechnicalError,
                ),
              );
            }
          }),
        ),
    ),
  );

  @Effect()
  deleteMandate$: Observable<Action> = this.actions$.pipe(
    ofType(ProductDetailsActions.DELETE_MANDATE),
    switchMap((action: DeleteMandate) =>
      this.mpayService
        .deleteMandate(
          action.payload.mandate.mandateId,
          action.payload.accessNumber,
        )
        .pipe(
          map(
            (iEntity: IEntity<any>) =>
              new DeleteMandateSuccess({
                mandate: action.payload.mandate,
                mpcUuid: action.payload.mpcUuid,
              }),
          ),
          catchError(() => of(new DeleteMandateError())),
        ),
    ),
  );

  @Effect()
  deepLinkOptionDetails$: Observable<Action> = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_DEEP_LINK_OPTION_DETAILS),
    switchMap((action: GetDeepLinkOptionDetails) =>
      this.productsService.getDeepLinkOptionDetails(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new GetDeepLinkOptionDetailsSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) => {
          if (error.status === 304) {
            return of(new GetDeepLinkOptionDetailsSuccessNoChange());
          } else {
            return of(
              new GetDeepLinkOptionDetailsFailed(
                error.errorData as TechnicalError,
              ),
            );
          }
        }),
      ),
    ),
  );

  @Effect()
  optionDetails$: Observable<Action> = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_OPTION_DETAILS),
    switchMap((action: GetOptionDetails) =>
      this.productsService.getOptionDetails(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) => new GetOptionDetailsSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) => {
          if (error.status === 304) {
            return of(new GetOptionDetailsSuccessNoChange());
          } else {
            return of(
              new GetOptionDetailsFailed(error.errorData as TechnicalError),
            );
          }
        }),
      ),
    ),
  );

  @Effect()
  ebuProductDetails$ = this.actions$.pipe(
    ofType(ProductDetailsActions.GET_EBU_PRODUCT_DETAILS),
    switchMap((action: GetEBUProductDetails) =>
      this.productsService.getEBUProductsDetails(action.payload).pipe(
        switchMap((iEntity: IEntity<any>) => {
          return [new GetEBUProductDetailsSuccess(iEntity.value)];
        }),
        catchError((error: IEntity<TechnicalError>) => {
          if (error.status === 304) {
            return of(new GetEBUProductDetailsSuccessNoChange());
          } else {
            return of(
              new GetEBUProductDetailsFailed(error.errorData as TechnicalError),
            );
          }
        }),
      ),
    ),
  );

  @Effect()
  updateEBUOptionActiveStatus$ = this.actions$.pipe(
    ofType(ProductDetailsActions.UPDATE_EBU_ACTIVE_OPTIONS),
    map(
      (action: ProductDetailsActions.UpdateEBUOptionActiveStatus) =>
        action.payload,
    ),
    withLatestFrom(this.store.select(getSelectedProductDetails)),
    mergeMap(([{ productId, optionId }, productDetails]) => {
      const selectedProduct = cloneDeep(productDetails);
      const selectedProductDetails =
        selectedProduct != null &&
        (selectedProduct.mobileProductDetails ||
          selectedProduct.tvProductDetails ||
          selectedProduct.telephonyProductDetails ||
          selectedProduct.internetProductDetails);
      if (selectedProductDetails.options) {
        const updatedOptions = this.productsService.updateEBUOptionActiveStatus(
          {
            options: selectedProductDetails.options,
            optionId,
          },
        );
        selectedProductDetails.options = Array.from(updatedOptions);
        return of(new UpdateEBUOptionActiveStatusSuccess(selectedProduct));
      }
    }),
  );

  constructor(
    private actions$: Actions,
    private productsService: ProductsService,
    private mpayService: MpayService,
    private store: Store<ApplicationState>,
  ) {}
}
